import { Feature, GeometryType, LineSegmentType } from "../../../core/geo/types";
import { GeometryCollection, LineString, Polygon } from "geojson";
import { isEmptyArray } from "../../../core/utils/array";
import { validateSegments } from "./validateSegment";

interface FeatureValidator {
    validateFeature: (feature: Feature) => void;
}

const validateCoordinatesNotSet = (geometry: Polygon | LineString) => {
    if (geometry.coordinates && geometry.coordinates.length > 0) {
        throw Error("Coordinates must be undefined or empty for Polygon or LineString geometries");
    }
};

const lineValidator: FeatureValidator = {
    validateFeature(feature: Feature) {
        const segments = feature.properties.segments;
        if (!segments) {
            throw new Error("Segments are mandatory for LineString geometry");
        }
        validateCoordinatesNotSet(feature.geometry as LineString);
        validateSegments(segments);
        if (segments.find((segment) => segment.type === LineSegmentType.FOLLOWS_GEO_BORDER)) {
            throw new Error(`'${LineSegmentType.FOLLOWS_GEO_BORDER}' segment type not allowed for LineString geometry`);
        }
        if (segments.length > 0 && segments.length < 2) {
            throw new Error("LineString must have at least 2 points");
        }
    },
};

const polygonValidator: FeatureValidator = {
    validateFeature(feature: Feature) {
        const segments = feature.properties.segments;
        if (!segments) {
            throw new Error("Segments are mandatory for Polygon geometry");
        }
        validateCoordinatesNotSet(feature.geometry as Polygon);
        validateSegments(segments);
        if (segments.length > 0 && segments.length < 3) {
            throw new Error("Polygon must have at least 3 points");
        }
    },
};

const geometryCollectionValidator: FeatureValidator = {
    validateFeature(feature: Feature) {
        const geometry = feature.geometry as GeometryCollection;
        if (feature.properties.isCircle && !isEmptyArray(geometry.geometries) && !feature.properties.radius) {
            throw new Error("Radius attribute is mandatory");
        }
    },
};

const voidValidator: FeatureValidator = {
    validateFeature() {},
};

const validators: { [key in GeometryType]: FeatureValidator } = {
    Point: voidValidator,
    LineString: lineValidator,
    Polygon: polygonValidator,
    GeometryCollection: geometryCollectionValidator,
    MultiLineString: voidValidator,
    MultiPoint: voidValidator,
    MultiPolygon: voidValidator,
};

export const validateFeature = (feature: Feature) => {
    validators[feature.geometry.type].validateFeature(feature);
};
